import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { preparePageNumber } from '../preparePageNumber';
import { correctPathname } from '../correctPathname';
import { setListingAppendType } from '../../actions/settings';
import { IPaginationItem } from '../../types/pagination';
import { ISettings } from '../../types/settings';

type TParams = {
  total: number;
  limit: number;
  totalParent?: number;
};

/**
 * Функционал для пагинации
 */
export const usePagination = <T extends TParams = TParams>({ total, limit, totalParent }: T) => {
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [activePageNumber, setActivePageNumber] = useState(preparePageNumber(searchParams.get('page') || ''));
  const history = useHistory();
  const totalPages = Math.ceil((totalParent || total) / limit) || 0;

  /**
   * Слушает изменение page в qs и обновляет номер активной страницы
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const qsPage = preparePageNumber(searchParams.get('page') || '');

    if (activePageNumber === qsPage) {
      return;
    }

    setActivePageNumber(qsPage);
  }, [activePageNumber, search]);

  /**
   * Устанавливает номер активной страницы.
   * Сохраняет в адресную строку
   */
  const changePageNumber = useCallback(
    (number: IPaginationItem['number'], type: ISettings['listingAppendType'] = 'set') => {
      if (number === 1) {
        searchParams.delete('page');
      } else {
        searchParams.set('page', String(number));
      }

      history.push(`${correctPathname(pathname)}?${searchParams.toString()}`);

      /** Запоминает в сторе тип обновления списка - догрузка/замена */
      dispatch(setListingAppendType(type));
    },
    [dispatch, history, pathname, searchParams],
  );

  return {
    activePageNumber,
    totalPages,
    changePageNumber,
  };
};
